import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBookOpen, FiUsers, FiTarget, FiArrowRight, FiStar, FiBarChart2, FiShoppingBag } = FiIcons;

function PlannerHome() {
  const navigate = useNavigate();

  // Update iframe height when component mounts
  useEffect(() => {
    if (window.updateIframeHeight) {
      setTimeout(() => window.updateIframeHeight(), 100);
    }
  }, []);

  const features = [
    {
      icon: FiTarget,
      title: "Science of Reading Based",
      description: "Built on evidence-based phonics instruction principles"
    },
    {
      icon: FiUsers,
      title: "Skill-Based Grouping",
      description: "Groups students by their lowest unmastered skill for targeted instruction"
    },
    {
      icon: FiBarChart2,
      title: "Assessment Integration",
      description: "Works with Quick Phonics Screener, DIBELS 8, CORE Phonics Survey, and Thrive Literacy Corner Small Group Assessment data"
    }
  ];

  const openShop = () => {
    window.open("https://shop.thriveedservices.com/", "_blank");
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-thrive-rose/30 to-thrive-rose/20 shadow-sm border-b"
      >
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-thrive-slate font-heading">Reading Groups Planner</h1>
            <p className="text-2xl text-gray-700 mt-3 font-body">K-5 Small Group Planning Assistant</p>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="flex-1 max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="text-4xl font-bold text-thrive-slate mb-4 font-heading">
              Transform Assessment Data into{' '}
              <span className="text-thrive-teal">Actionable Teaching Plans</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 font-body">
              Your friendly assistant for creating effective skill-based reading groups using the Science of Reading approach. Turn your assessment data into targeted instruction!
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-12"
          >
            <button
              onClick={() => navigate('/assessment')}
              className="bg-thrive-teal hover:bg-thrive-slate text-white px-8 py-4 rounded-xl text-lg font-semibold transition-colors duration-200 flex items-center gap-3 mx-auto group shadow-lg font-body"
            >
              Get Started
              <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:border-thrive-teal/30 transition-all"
            >
              <div className="bg-thrive-mint/30 p-3 rounded-lg w-fit mb-4">
                <SafeIcon icon={feature.icon} className="text-thrive-teal text-xl" />
              </div>
              <h3 className="text-lg font-semibold text-thrive-slate mb-2 font-body">{feature.title}</h3>
              <p className="text-gray-600 font-body">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Process Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 mb-12"
        >
          <h3 className="text-2xl font-bold text-thrive-slate mb-6 text-center font-heading">How It Works</h3>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: 1, title: "Input Assessment Data", desc: "Use QPS, DIBELS 8, CORE, or Thrive assessments" },
              { step: 2, title: "Analyze Skills", desc: "Find each student's lowest unmastered skill" },
              { step: 3, title: "Form Groups", desc: "Create skill-based groups with activities" },
              { step: 4, title: "Get Your Plan", desc: "Receive a complete teaching plan" }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="bg-thrive-peach/30 text-thrive-slate w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-3 font-body">
                  {item.step}
                </div>
                <h4 className="font-semibold text-thrive-slate mb-2 font-body">{item.title}</h4>
                <p className="text-sm text-gray-600 font-body">{item.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Shop Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl shadow-lg border border-thrive-peach p-8 mb-12"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl font-bold text-thrive-slate mb-2 font-heading">Need Science of Reading Resources?</h3>
              <p className="text-gray-600 mb-4 font-body">
                Find evidence-based activities, decodable texts, and assessment tools for your small groups.
              </p>
            </div>
            <button
              onClick={openShop}
              className="bg-thrive-peach hover:bg-thrive-rose text-thrive-slate px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2 font-body"
            >
              <SafeIcon icon={FiShoppingBag} />
              Visit Our Shop
            </button>
          </div>
        </motion.div>

        {/* Encouragement Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-gradient-to-r from-thrive-teal to-thrive-slate text-white rounded-xl p-8 text-center shadow-lg"
        >
          <SafeIcon icon={FiStar} className="text-3xl mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-2 font-heading">Ready to Create Amazing Reading Groups?</h3>
          <p className="text-lg opacity-90 mb-6 font-body">
            Remember: Groups are flexible—update as soon as students master their current skill!
          </p>
          <button
            onClick={() => navigate('/assessment')}
            className="bg-white text-thrive-teal hover:text-thrive-slate px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors shadow-md font-body"
          >
            Start Planning Now
          </button>
        </motion.div>
      </main>
    </div>
  );
}

export default PlannerHome;